import React from 'react';
import PropTypes from 'prop-types';

import * as Sentry from '@sentry/react';

import { Title, Image, Text } from '@mantine/core';

import ErrorBoundaryContext from 'context/ErrorBoundaryContext';
import {
  ERROR_ACTION,
  DEFAULT_ERROR_STATE,
  ERROR_PAGE_BUTTONS,
  ERROR_PAGE_SUBTITLES,
  ERROR_PAGE_TITLES,
  ERROR_MODAL_DEFAULTS,
  UNAUTHORIZED,
} from 'constants/error';

import { withPathname } from 'helpers/component';

import HalftoneIceCream from 'assets/images/icecream-fallen.png';

import Cover from 'primitives/Cover/Cover';
import ErrorPage from 'components/Errors/ErrorPage/ErrorPage';
import DialogModal from 'components/Modals/DialogModal/DialogModal';
import ErrorCard from 'components/Errors/ErrorCard/ErrorCard';
import ErrorButton from 'components/Errors/ErrorButton/ErrorButton';
import ErrorCardByType from 'components/Errors/ErrorCard/ErrorCardByType';

import { ErrorBoundaryError } from './errors';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = DEFAULT_ERROR_STATE;
    this.setError = this.setError.bind(this);
    this.getRenderValue = this.getRenderValue.bind(this);
  }

  static getDerivedStateFromError(error) {
    if (error instanceof ErrorBoundaryError) {
      return error.errorBoundaryData;
    }

    // Update state so the next render shows the fallback UI.
    return { hasError: true };
  }

  componentDidUpdate(prevProps, prevState) {
    const { pathname } = this.props;
    const { errorAction, errorCallback } = this.state;

    if (pathname !== prevProps.pathname) {
      // clear error state on route change
      this.setState({ ...DEFAULT_ERROR_STATE, errorPathname: undefined });
    } else if (errorAction !== prevState.errorAction && !!errorCallback) {
      // can call things like logout here
      errorCallback();
      this.setState(DEFAULT_ERROR_STATE);
    }
  }

  componentDidCatch(error) {
    Sentry.captureException(error);
    this.setState({ errorPathname: this.props.pathname });
  }

  setError(error) {
    this.setState(error);
  }

  getRenderValue() {
    const { hasError, errorAction, errorDetails, errorPathname } = this.state;
    const { children, fallback, pathname, rootErrorBoundary } = this.props;

    // the route changed since the error: render nothing until componentDidUpdate clears it
    if (hasError && errorPathname && errorPathname !== pathname) {
      return null;
    }

    if (!hasError) {
      return children;
    }

    if (errorAction === ERROR_ACTION.SHOW_UNAUTHORIZED_ERROR && !rootErrorBoundary) {
      return (
        <>
          {!!errorDetails?.coverTitle && <Cover title={errorDetails.coverTitle} />}
          <ErrorCardByType
            type={UNAUTHORIZED}
            buttonLabel={errorDetails?.buttonCta ?? ERROR_PAGE_BUTTONS.contactSupport}
          />
        </>
      );
    }

    if (errorAction === ERROR_ACTION.SHOW_ERROR_MODAL) {
      return (
        <DialogModal
          opened={hasError}
          noCloseOverlay
          title={errorDetails?.title || ERROR_MODAL_DEFAULTS.title}
          confirmLabel={errorDetails?.confirmLabel || ERROR_MODAL_DEFAULTS.confirmLabel}
          message={errorDetails?.message || ERROR_MODAL_DEFAULTS.message}
          close={() => {
            errorDetails?.onClose?.();
            window.location.reload();
          }}
          showCloseIcon={false}
        />
      );
    }

    if (errorAction === ERROR_ACTION.SHOW_LOGOUT_ERROR) {
      return (
        <>
          {!!errorDetails?.coverTitle && <Cover title={errorDetails?.coverTitle} />}
          <ErrorCard
            button={<ErrorButton label={ERROR_PAGE_BUTTONS.logout} noHeadquarterContext />}
          >
            <Image w={115} m="auto" src={HalftoneIceCream} />
            <Title ta="center" order={4} mb="0">
              OOPS
            </Title>
            <Text ta="center" variant="body-md">
              Something went wrong. Try logging in again to see this content.
            </Text>
          </ErrorCard>
        </>
      );
    }

    return (
      fallback || (
        <ErrorPage
          title={errorDetails?.title ?? ERROR_PAGE_TITLES.general}
          subtitle={errorDetails?.subtitle ?? ERROR_PAGE_SUBTITLES.general}
          buttonCta={errorDetails?.buttonCta ?? ERROR_PAGE_BUTTONS.goToLogin}
          isFullScreen
          noHeadquarterContext
        />
      )
    );
  }

  render() {
    return (
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      <ErrorBoundaryContext.Provider value={{ setError: this.setError }}>
        {this.getRenderValue()}
      </ErrorBoundaryContext.Provider>
    );
  }
}
// does not require `pathname` to be passed in
const WrappedErrorBoundary = withPathname(ErrorBoundary);
export default WrappedErrorBoundary;

ErrorBoundary.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.node,
  pathname: PropTypes.string.isRequired,
  rootErrorBoundary: PropTypes.bool,
};
